import { parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

// Calendar date (YYYY-MM-DD) in New York, `daysAgo` days back from today.
// Reports count EST days; toISOString() gives the UTC date, which is already
// tomorrow after 8 PM EST and depends on where the viewer is.
export const easternDateString = (daysAgo = 0) => {
  const [y, m, d] = formatInTimeZone(new Date(), "America/New_York", "yyyy-MM-dd")
    .split("-")
    .map(Number);
  return new Date(Date.UTC(y, m - 1, d - daysAgo)).toISOString().split("T")[0];
};

export const formatDate = (dateString) => {
  if (!dateString) return "N/A";

  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch (error) {
    console.error("Error formatting date:", error);
    return dateString; // Return the original string if there's an error
  }
};

export const formatDateTimeEDT = (datetimeString) => {
  if (!datetimeString) return "N/A";

  try {
    const date = new Date(datetimeString);

    // Format in EDT timezone (UTC-4)
    const options = {
      timeZone: "America/New_York",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    };

    return date.toLocaleString("en-US", options) + " EDT";
  } catch (error) {
    console.error("Error formatting datetime:", error);
    return datetimeString; // Return the original string if there's an error
  }
};

export const formatCurrency = (value) => {
  if (value === null || value === undefined) return "N/A";

  try {
    return `$${Number(value).toFixed(2)}`;
  } catch (error) {
    console.error("Error formatting currency:", error);
    return `$${value}`; // Return with dollar sign if there's an error
  }
};

export const formatPhoneNumber = (phone) => {
  if (!phone) return "N/A";

  // Remove all non-digits
  const cleaned = phone.replace(/\D/g, "");

  // Check if the input is of correct length
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);

  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }

  return phone; // Return the original value if it doesn't match the pattern
};

export const getEffectivenessColor = (value) => {
  if (value === null || value === undefined) return "var(--gray-300)";

  const rating = parseInt(value, 10);
  if (isNaN(rating)) return "var(--gray-300)";

  if (rating >= 8) return "#6aedca"; // Green
  if (rating >= 5) return "#f1b46f"; // Yellow/Orange
  if (rating >= 1) return "#f49090"; // Red

  return "var(--gray-300)";
};

export const getEffectivenessPercentage = (rating) => {
  if (rating === null || rating === undefined) return 0;

  const value = parseInt(rating, 10);
  if (isNaN(value) || value < 1) return 0;
  if (value > 10) return 100;

  // Convert 1-10 scale to 0-100%
  return (value - 1) * (100 / 9);
};

export const formatDateInEasternDT = (
  dateString,
  timeString,
  isPublic = true
) => {
  console.log("dateString est", dateString);
  console.log("timeString est", timeString);

  if (!dateString) return "Not available";

  try {
    let date;

    if (timeString) {
      const [year, month, day] = dateString
        .split("-")
        .map((num) => parseInt(num, 10));

      let hours = 0,
        minutes = 0,
        seconds = 0,
        milliseconds = 0;

      if (timeString) {
        const timeParts = timeString.split(":");
        hours = parseInt(timeParts[0] || 0, 10);
        minutes = parseInt(timeParts[1] || 0, 10);

        if (timeParts[2]) {
          if (timeParts[2].includes(".")) {
            const [secondsPart, fractionPart] = timeParts[2].split(".");
            seconds = parseInt(secondsPart, 10);
            milliseconds = parseInt((fractionPart + "000").substring(0, 3), 10);
          } else {
            seconds = parseInt(timeParts[2], 10);
          }
        }
      }

      date = new Date(
        Date.UTC(year, month - 1, day, hours, minutes, seconds, milliseconds)
      );
    } else {
      date = parseISO(dateString);
    }

    const formattedDate = formatInTimeZone(
      date,
      "America/New_York",
      "MMMM d, yyyy"
    );

    function isDSTinEasternTime(date) {
      const year = date.getFullYear();

      const marchDate = new Date(year, 2, 1);
      const marchDay = marchDate.getDay();
      const marchSecondSunday = marchDate.getDate() + (7 - marchDay) + 7;
      const dstStart = new Date(year, 2, marchSecondSunday, 2, 0, 0);

      const novDate = new Date(year, 10, 1);
      const novDay = novDate.getDay();
      const novFirstSunday = novDate.getDate() + ((7 - novDay) % 7);
      const dstEnd = new Date(year, 10, novFirstSunday, 2, 0, 0);

      const dateInET = new Date(
        date.toLocaleString("en-US", { timeZone: "America/New_York" })
      );

      const dstStartET = new Date(
        dstStart.toLocaleString("en-US", { timeZone: "America/New_York" })
      );
      const dstEndET = new Date(
        dstEnd.toLocaleString("en-US", { timeZone: "America/New_York" })
      );

      return dateInET >= dstStartET && dateInET < dstEndET;
    }

    return isPublic
      ? `${formattedDate}`
      : `${formattedDate} ${isDSTinEasternTime(date) ? "EDT" : "EST"}`;
  } catch (error) {
    console.error("Error formatting date:", error, dateString, timeString);
    return "Invalid date";
  }
};

export const formatTimeInEasternTI = (dateString, timeString) => {
  console.log("dateString est", dateString);
  console.log("timeString est", timeString);

  if (!timeString) return "N/A";

  try {
    let date;

    if (dateString && timeString) {
      // Handle both date and time inputs like formatDateInEasternDT
      const [year, month, day] = dateString
        .split("-")
        .map((num) => parseInt(num, 10));

      let hours = 0,
        minutes = 0,
        seconds = 0,
        milliseconds = 0;

      if (timeString) {
        const timeParts = timeString.split(":");
        hours = parseInt(timeParts[0] || 0, 10);
        minutes = parseInt(timeParts[1] || 0, 10);

        if (timeParts[2]) {
          if (timeParts[2].includes(".")) {
            const [secondsPart, fractionPart] = timeParts[2].split(".");
            seconds = parseInt(secondsPart, 10);
            milliseconds = parseInt((fractionPart + "000").substring(0, 3), 10);
          } else {
            seconds = parseInt(timeParts[2], 10);
          }
        }
      }

      date = new Date(
        Date.UTC(year, month - 1, day, hours, minutes, seconds, milliseconds)
      );
    } else if (
      timeString.includes("T") ||
      (timeString.includes("-") && timeString.length > 5)
    ) {
      // Handle ISO formatted date-times
      date = parseISO(timeString);
    } else {
      // Handle time-only strings (HH:MM:SS format)
      const today = new Date();
      const [hours, minutes, seconds] = timeString.split(":");

      let secondsValue = 0;
      let millisecondsValue = 0;

      if (seconds) {
        if (seconds.includes(".")) {
          const [secondsPart, fractionPart] = seconds.split(".");
          secondsValue = parseInt(secondsPart, 10);
          millisecondsValue = parseInt(
            (fractionPart + "000").substring(0, 3),
            10
          );
        } else {
          secondsValue = parseInt(seconds, 10);
        }
      }

      // Use Date.UTC to create a consistent date object
      date = new Date(
        Date.UTC(
          today.getUTCFullYear(),
          today.getUTCMonth(),
          today.getUTCDate(),
          parseInt(hours || 0, 10),
          parseInt(minutes || 0, 10),
          secondsValue,
          millisecondsValue
        )
      );
    }

    const formattedTime = formatInTimeZone(
      date,
      "America/New_York",
      "h:mm:ss a"
    );

    // Use the same DST detection function as in the date formatter
    function isDSTinEffect(date) {
      const year = date.getFullYear();

      // DST starts on second Sunday in March at 2 AM
      const marchDate = new Date(year, 2, 1);
      const marchDay = marchDate.getDay();
      const marchSecondSunday = marchDate.getDate() + (7 - marchDay) + 7;
      const dstStart = new Date(year, 2, marchSecondSunday, 2, 0, 0);

      // DST ends on first Sunday in November at 2 AM
      const novDate = new Date(year, 10, 1);
      const novDay = novDate.getDay();
      const novFirstSunday = novDate.getDate() + ((7 - novDay) % 7);
      const dstEnd = new Date(year, 10, novFirstSunday, 2, 0, 0);

      // Convert check date to Eastern Time for comparison
      const easternDate = new Date(
        date.toLocaleString("en-US", { timeZone: "America/New_York" })
      );

      // Compare with DST boundaries in Eastern Time
      const dstStartET = new Date(
        dstStart.toLocaleString("en-US", { timeZone: "America/New_York" })
      );
      const dstEndET = new Date(
        dstEnd.toLocaleString("en-US", { timeZone: "America/New_York" })
      );

      return easternDate >= dstStartET && easternDate < dstEndET;
    }

    return `${formattedTime} ${isDSTinEffect(date) ? "EDT" : "EST"}`;
  } catch (error) {
    console.error("Error formatting time:", error, timeString);
    return "Invalid time";
  }
};

export const formatTimeInEastern = (timeString) => {
  if (!timeString) return "N/A";

  try {
    let date;

    if (timeString.includes("T") || timeString.includes("-")) {
      date = parseISO(timeString);
    } else {
      const today = new Date();
      const [hours, minutes, seconds] = timeString.split(":");

      let secondsValue = seconds;
      if (seconds && seconds.includes(".")) {
        secondsValue = seconds.split(".")[0];
      }

      date = new Date(
        Date.UTC(
          today.getUTCFullYear(),
          today.getUTCMonth(),
          today.getUTCDate(),
          parseInt(hours || 0),
          parseInt(minutes || 0),
          parseInt(secondsValue || 0)
        )
      );
    }

    const formattedTime = formatInTimeZone(
      date,
      "America/New_York",
      "h:mm:ss a"
    );

    // Proper DST detection for Eastern Time
    function isDSTinEffect(date) {
      const year = date.getFullYear();

      // DST starts on second Sunday in March at 2 AM
      const marchDate = new Date(year, 2, 1);
      const marchDay = marchDate.getDay();
      const marchSecondSunday = marchDate.getDate() + (7 - marchDay) + 7;
      const dstStart = new Date(year, 2, marchSecondSunday, 2, 0, 0);

      // DST ends on first Sunday in November at 2 AM
      const novDate = new Date(year, 10, 1);
      const novDay = novDate.getDay();
      const novFirstSunday = novDate.getDate() + ((7 - novDay) % 7);
      const dstEnd = new Date(year, 10, novFirstSunday, 2, 0, 0);

      // Convert check date to Eastern Time for comparison
      const easternDate = new Date(
        date.toLocaleString("en-US", { timeZone: "America/New_York" })
      );

      // Compare with DST boundaries in Eastern Time
      const dstStartET = new Date(
        dstStart.toLocaleString("en-US", { timeZone: "America/New_York" })
      );
      const dstEndET = new Date(
        dstEnd.toLocaleString("en-US", { timeZone: "America/New_York" })
      );

      return easternDate >= dstStartET && easternDate < dstEndET;
    }

    return `${formattedTime} ${isDSTinEffect(date) ? "EDT" : "EST"}`;
  } catch (error) {
    console.error("Error formatting time:", error, timeString);
    return "Invalid time";
  }
};

export const formatDateInEastern = (dateString) => {
  console.log("dateString est", dateString);

  if (!dateString) return "Not available";

  try {
    const date = parseISO(dateString);
    console.log("date in ", date);
    const formattedDate = formatInTimeZone(
      date,
      "America/New_York",
      "MMMM d, yyyy"
    );

    const easternTime = new Date(
      date.toLocaleString("en-US", { timeZone: "America/New_York" })
    );
    const isDST =
      easternTime.getTimezoneOffset() <
      new Date(easternTime.getFullYear(), 0, 1).getTimezoneOffset();

    return `${formattedDate} ${isDST ? "EDT" : "EST"}`;
  } catch (error) {
    console.error("Error formatting date:", error, dateString);
    return "Invalid date";
  }
};

export const formatDateTimeInEastern = (dateString) => {
  console.log("dateString", dateString);

  if (!dateString) return "Not available";

  try {
    const date = parseISO(dateString);
    const formattedDateTime = formatInTimeZone(
      date,
      "America/New_York",
      "MMM d, yyyy h:mm a"
    );

    const easternTime = new Date(
      date.toLocaleString("en-US", { timeZone: "America/New_York" })
    );
    const isDST =
      easternTime.getTimezoneOffset() <
      new Date(easternTime.getFullYear(), 0, 1).getTimezoneOffset();

    return `${formattedDateTime} ${isDST ? "EDT" : "EST"}`;
  } catch (error) {
    console.error("Error formatting date and time:", error, dateString);
    return "Invalid date";
  }
};

// Compact date formatter for tables - modern, space-efficient format
export const formatDateCompact = (dateString, timeString) => {
  if (!dateString) return "N/A";

  try {
    let date;

    if (timeString) {
      const [year, month, day] = dateString
        .split("-")
        .map((num) => parseInt(num, 10));

      let hours = 0,
        minutes = 0,
        seconds = 0,
        milliseconds = 0;

      if (timeString) {
        const timeParts = timeString.split(":");
        hours = parseInt(timeParts[0] || 0, 10);
        minutes = parseInt(timeParts[1] || 0, 10);

        if (timeParts[2]) {
          if (timeParts[2].includes(".")) {
            const [secondsPart, fractionPart] = timeParts[2].split(".");
            seconds = parseInt(secondsPart, 10);
            milliseconds = parseInt((fractionPart + "000").substring(0, 3), 10);
          } else {
            seconds = parseInt(timeParts[2], 10);
          }
        }
      }

      date = new Date(
        Date.UTC(year, month - 1, day, hours, minutes, seconds, milliseconds)
      );
    } else {
      date = parseISO(dateString);
    }

    // Format as: Jul 28, '25
    const formattedDate = formatInTimeZone(
      date,
      "America/New_York",
      "MMM d, ''yy"
    );

    return formattedDate;
  } catch (error) {
    console.error(
      "Error formatting compact date:",
      error,
      dateString,
      timeString
    );
    return "Invalid date";
  }
};

export const formatPatientDob = (dobString) => {
  if (!dobString) return "N/A";

  try {
    // Parse the date string but don't apply timezone conversions
    // DOB should be timezone-agnostic since it's just a date, not datetime
    const dobParts = dobString.split("-");

    if (dobParts.length === 3) {
      const year = parseInt(dobParts[0], 10);
      const month = parseInt(dobParts[1], 10) - 1; // Month is 0-based in Date constructor
      const day = parseInt(dobParts[2], 10);

      // Create date using local date components without timezone conversion
      const date = new Date(year, month, day);

      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } else {
      // Fallback for other date formats
      const date = new Date(dobString);
      // Only use the date part, ignore time/timezone
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    }
  } catch (error) {
    console.error("Error formatting DOB:", error, dobString);
    return "Invalid date";
  }
};
